/**
 * Shorthand Breakdown Component
 * Displays quick estimate breakdown in agent chat
 */

import React from 'react';
import '../styles/SimplifiedEstimate.css';

interface ShorthandBreakdownProps {
  materialCost: number;
  laborHours: number;
  pmLaborRate: number;
  pmHours: number;
  laborRate: number;
  overhead: number;
  profit: number;
  materialTaxRate: number;
  // If user specified these in the shorthand prompt, use those instead
  shorthandLaborRate?: number;
  shorthandOverhead?: number;
  shorthandProfit?: number;
  shorthandMaterialTaxRate?: number;
}

export const ShorthandBreakdown: React.FC<ShorthandBreakdownProps> = ({
  materialCost,
  laborHours,
  pmLaborRate,
  pmHours,
  laborRate,
  overhead,
  profit,
  materialTaxRate,
  shorthandLaborRate,
  shorthandOverhead,
  shorthandProfit,
  shorthandMaterialTaxRate
}) => {
  const formatCurrency = (value: number): string => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };
  
  // Use shorthand-specific rates if provided, otherwise use global rates
  const effectiveLaborRate = shorthandLaborRate ?? laborRate;
  const effectiveOverhead = shorthandOverhead ?? overhead;
  const effectiveProfit = shorthandProfit ?? profit;
  const effectiveTaxRate = shorthandMaterialTaxRate ?? materialTaxRate;
  
  // Calculate breakdown
  const laborCost = laborHours * effectiveLaborRate;
  const materialCostWithTax = materialCost * (1 + effectiveTaxRate / 100);
  const pmCost = pmLaborRate * pmHours;
  const subtotal = laborCost + materialCostWithTax + pmCost;
  const overheadAmount = subtotal * (effectiveOverhead / 100);
  const profitAmount = (subtotal + overheadAmount) * (effectiveProfit / 100);
  const total = subtotal + overheadAmount + profitAmount;
  
  return (
    <div className="shorthand-section">
      <div className="shorthand-card">
        <h4 style={{ margin: '0 0 1rem 0', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-muted)' }}>SHORTHAND ESTIMATE</h4>
        
        {/* Input Summary */}
        <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '6px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.8125rem' }}>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Material Cost:</span>
              <span style={{ fontWeight: 600, marginLeft: '0.5rem' }}>${formatCurrency(materialCost)}</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Labor Hours:</span>
              <span style={{ fontWeight: 600, marginLeft: '0.5rem' }}>{laborHours} hrs</span>
            </div>
            {pmLaborRate > 0 && (
              <div>
                <span style={{ color: 'var(--text-muted)' }}>PM Rate:</span>
                <span style={{ fontWeight: 600, marginLeft: '0.5rem' }}>${pmLaborRate}/hr</span>
              </div>
            )}
            {pmHours > 0 && (
              <div>
                <span style={{ color: 'var(--text-muted)' }}>PM Hours:</span>
                <span style={{ fontWeight: 600, marginLeft: '0.5rem' }}>{pmHours} hrs</span>
              </div>
            )}
            <div>
              <span style={{ color: 'var(--text-muted)' }}>Labor Rate:</span>
              <span style={{ fontWeight: 600, marginLeft: '0.5rem' }}>${effectiveLaborRate}/hr</span>
            </div>
          </div>
        </div>
        
        {/* Breakdown */}
        <div className="shorthand-breakdown">
          <div className="shorthand-row">
            <span className="shorthand-label">Subtotal</span>
            <span className="shorthand-value">${formatCurrency(subtotal)}</span>
          </div>
          <div className="shorthand-row">
            <span className="shorthand-label">
              Material Tax <span className="material-tax-badge">{effectiveTaxRate}%</span>
            </span>
            <span className="shorthand-value">${formatCurrency(materialCost * (effectiveTaxRate / 100))}</span>
          </div>
          <div className="shorthand-row">
            <span className="shorthand-label">
              Overhead <span className="overhead-badge">{effectiveOverhead}%</span>
            </span>
            <span className="shorthand-value">${formatCurrency(overheadAmount)}</span>
          </div>
          <div className="shorthand-row">
            <span className="shorthand-label">
              Net Profit <span className="profit-badge">{effectiveProfit}%</span>
            </span>
            <span className="shorthand-value profit-value">${formatCurrency(profitAmount)}</span>
          </div>
          <div className="shorthand-total-row">
            <span>Total</span>
            <span>${formatCurrency(total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

